import { useApolloClient } from '@apollo/client';
import { Popover, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import { ReactComponent as ViewIcon } from '../assets/svg/eye-solid.svg';
import {
  DYNAMIC_CONFIG_TYPES,
  SORT,
  TAGS_CONFIG_SUBTYPES,
  TOY_RESERVATION_RULE_TYPES,
} from '../common/constants';
import {
  DYNAMIC_CONFIG_ITEMS,
  TOY_RESERVATION_RULE_USER,
} from '../modules/configs/modules/graphql/Queries';
import {
  getUserPrimaryLabel,
  getUserSecondaryLabel,
} from '../modules/configs/modules/toy/components/ReservationUserSelect';
import {
  getRuleSummary,
  getRuleTypeLabel,
} from '../modules/configs/modules/toy/toyReservationRuleUtils';

const PAGE_SIZE = 50;
const MAX_PAGES = 10;

const getTagIds = (rule) =>
  rule?.type === TOY_RESERVATION_RULE_TYPES?.USER_TAG?.value
    ? rule?.config?.tagIds || []
    : [];

const getUserIds = (rule) =>
  rule?.type === TOY_RESERVATION_RULE_TYPES?.SPECIFIC_USER?.value
    ? rule?.config?.userIds || []
    : [];

const ReservationRuleDetailsPopover = ({ rule }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tagLabelById, setTagLabelById] = useState({});
  const [usersById, setUsersById] = useState({});

  const client = useApolloClient();

  const loadTagLabels = async () => {
    const map = {};
    let skip = 0;
    let total = 0;
    let page = 0;

    do {
      // eslint-disable-next-line no-await-in-loop
      const result = await client?.query({
        query: DYNAMIC_CONFIG_ITEMS,
        fetchPolicy: 'network-only',
        variables: {
          filter: {
            configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
            configSubtypes: [TAGS_CONFIG_SUBTYPES?.USERS?.value],
            skip,
            limit: PAGE_SIZE,
          },
          sort: { field: 'createdAt', order: SORT?.DESC },
        },
      });

      const rows = result?.data?.dynamicConfigItems?.data || [];
      total = result?.data?.dynamicConfigItems?.count || 0;
      rows?.forEach((tag) => {
        map[tag?.id] = `${tag?.value ? `${tag?.value} ` : ''}${tag?.itemKey}`;
      });

      if (!rows?.length) break;
      skip += rows?.length;
      page += 1;
    } while (skip < total && page < MAX_PAGES);

    setTagLabelById(map);
  };

  const loadUsers = async () => {
    const map = {};

    await Promise.all(
      getUserIds(rule)
        ?.filter(Boolean)
        ?.map((id) =>
          client
            ?.query({
              query: TOY_RESERVATION_RULE_USER,
              fetchPolicy: 'network-only',
              variables: { where: { id } },
            })
            .then((result) => {
              map[id] = result?.data?.user || null;
            })
            .catch(() => {
              map[id] = null;
            }),
        ),
    );

    setUsersById(map);
  };

  const handleOpenChange = async (open) => {
    setIsOpen(open);
    if (!open) return;

    const needsTags = getTagIds(rule)?.length > 0;
    const needsUsers = getUserIds(rule)?.length > 0;
    if (!needsTags && !needsUsers) return;

    setIsLoading(true);
    try {
      await Promise.all([
        needsTags ? loadTagLabels() : null,
        needsUsers ? loadUsers() : null,
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderUserTag = () => {
    const tagIds = getTagIds(rule);
    if (!tagIds?.length) return <div className="rule-details-line">-</div>;

    const logic = rule?.config?.logic || 'OR';

    return (
      <div className="rule-details-tags">
        {tagIds?.map((id, index) => (
          <React.Fragment key={id}>
            {index > 0 && <span className="rule-details-joiner">{logic}</span>}
            <span className="rule-details-tag">{tagLabelById?.[id] || id}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderSpecificUser = () => {
    const userIds = getUserIds(rule);
    const contactNos = rule?.config?.contactNos || [];

    if (!userIds?.length && !contactNos?.length) {
      return <div className="rule-details-line">-</div>;
    }

    return (
      <>
        {userIds?.length > 0 && (
          <div className="rule-details-section">
            <div className="rule-details-heading">Users</div>
            {userIds?.map((id) => {
              const user = usersById?.[id];
              if (!user) {
                return (
                  <div key={id} className="rule-details-line">
                    {id}
                  </div>
                );
              }
              const secondary = getUserSecondaryLabel(user);
              return (
                <div key={id} className="rule-details-line">
                  {getUserPrimaryLabel(user)}
                  {secondary ? (
                    <span className="rule-details-muted"> · {secondary}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {contactNos?.length > 0 && (
          <div className="rule-details-section">
            <div className="rule-details-heading">Phone Numbers</div>
            {contactNos?.map((contactNo) => (
              <div key={contactNo} className="rule-details-line">
                {contactNo}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="d-flex justify-center p-8">
          <Spin size="small" />
        </div>
      );
    }

    const renderBody = () => {
      switch (rule?.type) {
        case TOY_RESERVATION_RULE_TYPES?.SPECIFIC_USER?.value:
          return renderSpecificUser();
        case TOY_RESERVATION_RULE_TYPES?.USER_TAG?.value:
          return renderUserTag();
        default:
          return (
            <div className="rule-details-line">{getRuleSummary(rule)}</div>
          );
      }
    };

    return (
      <div className="rule-details-popover">
        <div className="rule-details-type">{getRuleTypeLabel(rule?.type)}</div>
        {renderBody()}
      </div>
    );
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomLeft"
      title={rule?.name}
      content={renderContent()}
      overlayClassName="rule-details-overlay"
    >
      <Tooltip title="See details" placement="top" zIndex={4}>
        <ViewIcon className="pointer svg-icon" />
      </Tooltip>
    </Popover>
  );
};

export default ReservationRuleDetailsPopover;
